import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from '@tanstack/react-router';
import { useWorkout } from '../hooks/useWorkout';
import RouteMap from '../components/workout/RouteMap';
import StreamCharts from '../components/StreamCharts';
import {
  dayjs,
  formatDistance,
  formatFriendlyDuration,
  formatWorkoutPace,
} from '../lib/utils';
import {
  Activity,
  Share2,
  RefreshCw,
  Trash2,
  Link2,
  Check,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useSyncWorkout } from '../hooks/mutations/useSyncWorkout';
import { useDeleteWorkout } from '../hooks/mutations/useDeleteWorkout';
import { useAccessToken } from '../hooks/useProfile';
import WorkoutActionBar from '../components/WorkoutActionBar';

const splitDisplayUnit = (display: string) => {
  const match = display.match(/^(.*?)(?:\s*(mi|bpm|ft|\/mi|\/100 yd))$/i);
  if (!match) return { value: display, unit: '' };
  return { value: match[1].trim(), unit: match[2].toUpperCase() };
};

const renderMetric = (display: string) => {
  const metric = splitDisplayUnit(display);
  return (
    <>
      {metric.value}
      {metric.unit ? (
        <span className="ml-1 text-[11px] font-black uppercase tracking-wide text-brand-slate">
          {metric.unit}
        </span>
      ) : null}
    </>
  );
};

const formatInsightText = (text: string) => {
  const patterns = [
    /gain of \d+(?:\.\d+)? feet/gi,
    /fastest split(?: being)?[^.,;]*/gi,
    /average pace(?: of)?[^.,;]*/gi,
    /elevation gain(?: of)?[^.,;]*/gi,
    /heart rate(?: of)?[^.,;]*/gi,
  ];

  const ranges = patterns
    .flatMap((pattern) =>
      [...text.matchAll(pattern)].map((match) => ({
        start: match.index ?? 0,
        end: (match.index ?? 0) + match[0].length,
      })),
    )
    .sort((a, b) => a.start - b.start)
    .filter(
      (range, index, ranges) =>
        index === 0 || range.start >= ranges[index - 1].end,
    );

  if (ranges.length === 0) return text;

  const parts: Array<string | JSX.Element> = [];
  let cursor = 0;
  ranges.forEach((range, index) => {
    if (range.start > cursor) parts.push(text.slice(cursor, range.start));
    parts.push(
      <strong key={`${range.start}-${index}`}>
        {text.slice(range.start, range.end)}
      </strong>,
    );
    cursor = range.end;
  });
  if (cursor < text.length) parts.push(text.slice(cursor));
  return parts;
};

const formatNotes = (description: string) =>
  description
    .split(/(?<=[.!?])\s+/)
    .map((sentence) => sentence.trim())
    .filter(Boolean);

const WorkoutDetail = () => {
  const { id } = useParams({ from: '/workouts/$id' });
  const navigate = useNavigate();
  const { data: workout, isLoading, error } = useWorkout(id);
  const syncWorkout = useSyncWorkout();
  const deleteWorkout = useDeleteWorkout();
  const accessToken = useAccessToken();
  const [generatedNotes, setGeneratedNotes] = useState('');
  const [shareStatus, setShareStatus] = useState<'idle' | 'sharing' | 'copied'>(
    'idle',
  );

  useEffect(() => {
    if (!workout?.description) {
      setGeneratedNotes('');
      return;
    }

    setGeneratedNotes(workout.description || '');
  }, [workout?.description]);

  const noteItems = useMemo(
    () => (generatedNotes ? formatNotes(generatedNotes) : []),
    [generatedNotes],
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  if (error || !workout) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] gap-4">
        <div className="text-gray-500">Unable to load workout</div>
        <Button variant="outline" onClick={() => navigate({ to: '/workouts' })}>
          Back to Activities
        </Button>
      </div>
    );
  }

  const d = dayjs(workout.completedAt);
  const hasMap =
    workout.polylineEncoded ||
    (workout.startLatitude && workout.startLongitude);
  const workoutTime = workout.elapsedTime || workout.movingTime || 0;

  const handleSync = () => {
    syncWorkout.mutate(
      { workoutId: workout.id },
      {
        onSuccess: () => window.dispatchEvent(new CustomEvent('showToast', { detail: { message: 'Workout resynced.', type: 'success' } })),
        onError: (error) => window.dispatchEvent(new CustomEvent('showToast', { detail: { message: error.message, type: 'error' } })),
      },
    );
  };

  const handleDelete = () => {
    if (!confirm('Delete this activity? This cannot be undone.')) return;
    deleteWorkout.mutate(
      { workoutId: workout.id },
      {
        onSuccess: () => navigate({ to: '/workouts' }),
        onError: (error) => window.dispatchEvent(new CustomEvent('showToast', { detail: { message: error.message, type: 'error' } })),
      },
    );
  };

  const handleShare = async () => {
    setShareStatus('sharing');
    try {
      const response = await fetch(
        `${import.meta.env.REACT_APP_API_SERVICE_URI}/api/workouts/${workout.id}/share`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${accessToken.data || ''}` },
        },
      );
      if (!response.ok) throw new Error('Unable to create share link');
      const result = await response.json();

      if (navigator.share) {
        await navigator.share({
          title: workout.title || 'RaceTrainer workout',
          text: 'View this workout on RaceTrainer',
          url: result.url,
        });
        setShareStatus('idle');
        return;
      }

      await navigator.clipboard.writeText(result.url);
      setShareStatus('copied');
      window.setTimeout(() => setShareStatus('idle'), 2500);
    } catch (error) {
      setShareStatus('idle');
      if (error instanceof DOMException && error.name === 'AbortError') return;
      window.dispatchEvent(new CustomEvent('showToast', { detail: {
        message: error instanceof Error ? error.message : 'Unable to share workout',
        type: 'error',
      } }));
    }
  };

  return (
    <div className="mx-auto max-w-5xl space-y-6 px-1 pb-6 md:px-0">
      <WorkoutActionBar
        backLabel="Back to activities"
        onBack={() => navigate({ to: '/workouts' })}
        actions={[
          {
            label:
              shareStatus === 'copied'
                ? 'Link copied'
                : shareStatus === 'sharing'
                  ? 'Creating share link'
                  : 'Share workout',
            icon:
              shareStatus === 'copied' ? (
                <Check className="h-5 w-5" />
              ) : (
                <Share2 className="h-5 w-5" />
              ),
            onClick: handleShare,
            disabled: shareStatus === 'sharing',
          },
          {
            label: syncWorkout.isPending
              ? 'Resyncing workout'
              : 'Resync workout',
            icon: (
              <RefreshCw
                className={`h-5 w-5 ${syncWorkout.isPending ? 'animate-spin' : ''}`}
              />
            ),
            onClick: handleSync,
            disabled: syncWorkout.isPending,
          },
          {
            label: deleteWorkout.isPending
              ? 'Deleting workout'
              : 'Delete workout',
            icon: <Trash2 className="h-5 w-5" />,
            onClick: handleDelete,
            disabled: deleteWorkout.isPending,
            destructive: true,
          },
        ]}
      />

      {/* Title and date */}
      <div className="detail-heading clear-both">
        <p className="eyebrow">Completed activity</p>
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="text-3xl font-bold tracking-tight text-brand-midnight">
            {workout.title || 'Untitled Workout'}
          </h1>
          {workout.planned_workout_id ? (
            <button
              type="button"
              onClick={() =>
                navigate({
                  to: '/plan/workout/$id',
                  params: { id: String(workout.planned_workout_id) },
                })
              }
              className="inline-flex items-center gap-1 rounded-full bg-brand-blueSoft px-2 py-0.5 text-xs font-medium text-brand-midnight transition-colors hover:bg-brand-blueSoft/70"
            >
              <Link2 className="h-3 w-3" />
              Linked
            </button>
          ) : null}
        </div>
        <p className="text-brand-slate mt-1">
          {d.isValid() ? d.format('dddd, MMMM D, YYYY · h:mm A') : ''}
        </p>
        {workout.type ? (
          <span className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-brand-midnight px-3 py-1 text-sm font-semibold capitalize text-white">
            <Activity className="h-3.5 w-3.5 text-brand-volt" />
            {workout.type}
          </span>
        ) : null}
      </div>

      {/* Route map and stats */}
      <div className="space-y-6">
        {/* Route map */}
        {hasMap ? (
          <div className="surface-card rounded-2xl p-2">
            <RouteMap
              polylineEncoded={workout.polylineEncoded}
              startLatitude={workout.startLatitude}
              startLongitude={workout.startLongitude}
              endLatitude={workout.endLatitude}
              endLongitude={workout.endLongitude}
              className="h-64 w-full rounded-xl overflow-hidden md:h-80"
              showMarkers={false}
              showAttribution={false}
            />
          </div>
        ) : null}

        {/* Stats */}
        <div className="surface-card p-4 sm:p-5">
          <div className="workout-stat-grid grid grid-cols-2 gap-y-4 md:grid-cols-6">
            <div className="border-l border-gray-100 px-3 dark:border-gray-100">
              <div className="text-xs text-brand-slate uppercase tracking-wide">
                Distance
              </div>
              <div className="text-lg font-semibold text-brand-midnight">
                {renderMetric(formatDistance(workout.distance || 0))}
              </div>
            </div>
            <div className="px-3">
              <div className="text-xs text-brand-slate uppercase tracking-wide">
                Time
              </div>
              <div className="text-lg font-semibold text-brand-midnight">
                {formatFriendlyDuration(workoutTime)}
              </div>
            </div>
            <div className="px-3">
              <div className="text-xs text-brand-slate uppercase tracking-wide">
                Avg Pace
              </div>
              <div className="text-lg font-semibold text-brand-midnight">
                {renderMetric(
                  formatWorkoutPace(
                    workout.type,
                    workout.distance || 0,
                    workout.movingTime || workout.elapsedTime || 0,
                  ),
                )}
              </div>
            </div>
            <div className="px-3">
              <div className="text-xs text-brand-slate uppercase tracking-wide">
                Avg HR
              </div>
              <div className="text-lg font-semibold text-brand-midnight">
                {workout.averageHeartRate && workout.averageHeartRate > 0
                  ? renderMetric(`${Math.round(workout.averageHeartRate)} bpm`)
                  : 'N/A'}
              </div>
            </div>
            <div className="px-3">
              <div className="text-xs text-brand-slate uppercase tracking-wide">
                Max HR
              </div>
              <div className="text-lg font-semibold text-brand-midnight">
                {workout.maxHeartRate && workout.maxHeartRate > 0
                  ? renderMetric(`${Math.round(workout.maxHeartRate)} bpm`)
                  : 'N/A'}
              </div>
            </div>
            <div className="px-3 last:pr-0">
              <div className="text-xs text-brand-slate uppercase tracking-wide">
                Elevation
              </div>
              <div className="text-lg font-semibold text-brand-midnight">
                {workout.elevationGain && workout.elevationGain > 0
                  ? renderMetric(
                      `${Math.round(workout.elevationGain * 3.28084)} ft`,
                    )
                  : 'N/A'}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Description */}
      {workout.description ? (
        <div className="surface-card p-5 sm:p-6">
          <h3 className="mb-3 text-sm font-bold uppercase tracking-[0.16em] text-brand-slate">
            Notes
          </h3>
          <ul className="space-y-2 text-sm leading-6 text-gray-700 dark:text-white">
            {noteItems.map((item, index) => (
              <li key={index} className="flex gap-2">
                <span className="mt-2 h-1.5 w-1.5 flex-none rounded-full bg-brand-volt" />
                <span>{formatInsightText(item)}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {/* Activity Stream Charts */}
      {workout.activityStreams ? (
        workout.activityStreams.length > 0 ? (
          <StreamCharts
            streams={workout.activityStreams}
            intervals={workout.activityIntervals}
          />
        ) : null
      ) : null}
    </div>
  );
};

export default WorkoutDetail;
